<template>
  <div class="phone-input-container" ref="dropdownContainer">
    <div class="country-code-selector">
      <button
        type="button"
        @click="toggleDropdown"
        class="country-code-button"
        :class="{ 'is-invalid': hasError, 'is-open': isDropdownOpen }"
        :disabled="disabled"
      >
        <span class="selected-dial-code">{{ selectedCountryDialCode }}</span>
        <svg
          class="dropdown-arrow"
          :class="{ rotate: isDropdownOpen }"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    <div class="phone-number-input">
      <input
        :value="phoneNumber"
        @input="updatePhoneNumber"
        type="tel"
        class="form-control"
        :class="{ 'is-invalid': hasError }"
        :placeholder="placeholder"
        :disabled="disabled"
      />
    </div>
    <div v-if="isDropdownOpen" class="country-dropdown" :class="{ 'is-invalid': hasError }">
      <div class="dropdown-search">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search countries..."
          class="search-input"
          @click.stop
        />
      </div>
      <div class="dropdown-list">
        <button
          v-for="(country, index) in filteredCountries"
          :key="index"
          type="button"
          @click="selectCountry(country)"
          class="dropdown-item"
          :class="{ 'is-selected': country.code === countryCode }"
        >
          <span class="country-flag">{{ country.flag }}</span>
          <span class="country-name">{{ country.name }}</span>
          <span class="country-dial-code">({{ country.dialCode }})</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PhoneInput',
  props: {
    value: {
      type: String,
      default: '',
    },
    countryCode: {
      type: String,
      default: '234',
    },
    placeholder: {
      type: String,
      default: 'Phone number',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDropdownOpen: false,
      searchQuery: '',
      countries: [
        { name: 'Afghanistan', code: '93', flag: '🇦🇫', dialCode: '+93' },
        { name: 'Albania', code: '355', flag: '🇦🇱', dialCode: '+355' },
        { name: 'Algeria', code: '213', flag: '🇩🇿', dialCode: '+213' },
        { name: 'American Samoa', code: '1684', flag: '🇦🇸', dialCode: '+1684' },
        { name: 'Andorra', code: '376', flag: '🇦🇩', dialCode: '+376' },
        { name: 'Angola', code: '244', flag: '🇦🇴', dialCode: '+244' },
        { name: 'Anguilla', code: '1264', flag: '🇦🇮', dialCode: '+1264' },
        { name: 'Antarctica', code: '672', flag: '🇦🇶', dialCode: '+672' },
        { name: 'Antigua and Barbuda', code: '1268', flag: '🇦🇬', dialCode: '+1268' },
        { name: 'Argentina', code: '54', flag: '🇦🇷', dialCode: '+54' },
        { name: 'Armenia', code: '374', flag: '🇦🇲', dialCode: '+374' },
        { name: 'Aruba', code: '297', flag: '🇦🇼', dialCode: '+297' },
        { name: 'Australia', code: '61', flag: '🇦🇺', dialCode: '+61' },
        { name: 'Austria', code: '43', flag: '🇦🇹', dialCode: '+43' },
        { name: 'Azerbaijan', code: '994', flag: '🇦🇿', dialCode: '+994' },
        { name: 'Bahamas', code: '1242', flag: '🇧🇸', dialCode: '+1242' },
        { name: 'Bahrain', code: '973', flag: '🇧🇭', dialCode: '+973' },
        { name: 'Bangladesh', code: '880', flag: '🇧🇩', dialCode: '+880' },
        { name: 'Barbados', code: '1246', flag: '🇧🇧', dialCode: '+1246' },
        { name: 'Belarus', code: '375', flag: '🇧🇾', dialCode: '+375' },
        { name: 'Belgium', code: '32', flag: '🇧🇪', dialCode: '+32' },
        { name: 'Belize', code: '501', flag: '🇧🇿', dialCode: '+501' },
        { name: 'Benin', code: '229', flag: '🇧🇯', dialCode: '+229' },
        { name: 'Bermuda', code: '1441', flag: '🇧🇲', dialCode: '+1441' },
        { name: 'Bhutan', code: '975', flag: '🇧🇹', dialCode: '+975' },
        { name: 'Bolivia', code: '591', flag: '🇧🇴', dialCode: '+591' },
        { name: 'Bosnia and Herzegovina', code: '387', flag: '🇧🇦', dialCode: '+387' },
        { name: 'Botswana', code: '267', flag: '🇧🇼', dialCode: '+267' },
        { name: 'Brazil', code: '55', flag: '🇧🇷', dialCode: '+55' },
        { name: 'British Indian Ocean Territory', code: '246', flag: '🇮🇴', dialCode: '+246' },
        { name: 'Brunei Darussalam', code: '673', flag: '🇧🇳', dialCode: '+673' },
        { name: 'Bulgaria', code: '359', flag: '🇧🇬', dialCode: '+359' },
        { name: 'Burkina Faso', code: '226', flag: '🇧🇫', dialCode: '+226' },
        { name: 'Burundi', code: '257', flag: '🇧🇮', dialCode: '+257' },
        { name: 'Cambodia', code: '855', flag: '🇰🇭', dialCode: '+855' },
        { name: 'Cameroon', code: '237', flag: '🇨🇲', dialCode: '+237' },
        { name: 'Canada', code: '1', flag: '🇨🇦', dialCode: '+1' },
        { name: 'Cape Verde', code: '238', flag: '🇨🇻', dialCode: '+238' },
        { name: 'Cayman Islands', code: '1345', flag: '🇰🇾', dialCode: '+1345' },
        { name: 'Central African Republic', code: '236', flag: '🇨🇫', dialCode: '+236' },
        { name: 'Chad', code: '235', flag: '🇹🇩', dialCode: '+235' },
        { name: 'Chile', code: '56', flag: '🇨🇱', dialCode: '+56' },
        { name: 'China', code: '86', flag: '🇨🇳', dialCode: '+86' },
        { name: 'Christmas Island', code: '61', flag: '🇨🇽', dialCode: '+61' },
        { name: 'Cocos (Keeling) Islands', code: '61', flag: '🇨🇨', dialCode: '+61' },
        { name: 'Colombia', code: '57', flag: '🇨🇴', dialCode: '+57' },
        { name: 'Comoros', code: '269', flag: '🇰🇲', dialCode: '+269' },
        { name: 'Congo', code: '242', flag: '🇨🇬', dialCode: '+242' },
        {
          name: 'Congo, Democratic Republic of the',
          code: '243',
          flag: '🇨🇩',
          dialCode: '+243',
        },
        { name: 'Cook Islands', code: '682', flag: '🇨🇰', dialCode: '+682' },
        { name: 'Costa Rica', code: '506', flag: '🇨🇷', dialCode: '+506' },
        { name: "Cote D'Ivoire", code: '225', flag: '🇨🇮', dialCode: '+225' },
        { name: 'Croatia', code: '385', flag: '🇭🇷', dialCode: '+385' },
        { name: 'Cuba', code: '53', flag: '🇨🇺', dialCode: '+53' },
        { name: 'Cyprus', code: '357', flag: '🇨🇾', dialCode: '+357' },
        { name: 'Czech Republic', code: '420', flag: '🇨🇿', dialCode: '+420' },
        { name: 'Denmark', code: '45', flag: '🇩🇰', dialCode: '+45' },
        { name: 'Djibouti', code: '253', flag: '🇩🇯', dialCode: '+253' },
        { name: 'Dominica', code: '1767', flag: '🇩🇲', dialCode: '+1767' },
        { name: 'Dominican Republic', code: '1809', flag: '🇩🇴', dialCode: '+1809' },
        { name: 'Ecuador', code: '593', flag: '🇪🇨', dialCode: '+593' },
        { name: 'Egypt', code: '20', flag: '🇪🇬', dialCode: '+20' },
        { name: 'El Salvador', code: '503', flag: '🇸🇻', dialCode: '+503' },
        { name: 'Equatorial Guinea', code: '240', flag: '🇬🇶', dialCode: '+240' },
        { name: 'Eritrea', code: '291', flag: '🇪🇷', dialCode: '+291' },
        { name: 'Estonia', code: '372', flag: '🇪🇪', dialCode: '+372' },
        { name: 'Ethiopia', code: '251', flag: '🇪🇹', dialCode: '+251' },
        { name: 'Falkland Islands (Malvinas)', code: '500', flag: '🇫🇰', dialCode: '+500' },
        { name: 'Faroe Islands', code: '298', flag: '🇫🇴', dialCode: '+298' },
        { name: 'Fiji', code: '679', flag: '🇫🇯', dialCode: '+679' },
        { name: 'Finland', code: '358', flag: '🇫🇮', dialCode: '+358' },
        { name: 'France', code: '33', flag: '🇫🇷', dialCode: '+33' },
        { name: 'French Guiana', code: '594', flag: '🇬🇫', dialCode: '+594' },
        { name: 'French Polynesia', code: '689', flag: '🇵🇫', dialCode: '+689' },
        { name: 'French Southern Territories', code: '262', flag: '🇹🇫', dialCode: '+262' },
        { name: 'Gabon', code: '241', flag: '🇬🇦', dialCode: '+241' },
        { name: 'Gambia', code: '220', flag: '🇬🇲', dialCode: '+220' },
        { name: 'Georgia', code: '995', flag: '🇬🇪', dialCode: '+995' },
        { name: 'Germany', code: '49', flag: '🇩🇪', dialCode: '+49' },
        { name: 'Ghana', code: '233', flag: '🇬🇭', dialCode: '+233' },
        { name: 'Gibraltar', code: '350', flag: '🇬🇮', dialCode: '+350' },
        { name: 'Greece', code: '30', flag: '🇬🇷', dialCode: '+30' },
        { name: 'Greenland', code: '299', flag: '🇬🇱', dialCode: '+299' },
        { name: 'Grenada', code: '1473', flag: '🇬🇩', dialCode: '+1473' },
        { name: 'Guadeloupe', code: '590', flag: '🇬🇵', dialCode: '+590' },
        { name: 'Guam', code: '1671', flag: '🇬🇺', dialCode: '+1671' },
        { name: 'Guatemala', code: '502', flag: '🇬🇹', dialCode: '+502' },
        { name: 'Guernsey', code: '44', flag: '🇬🇬', dialCode: '+44' },
        { name: 'Guinea', code: '224', flag: '🇬🇳', dialCode: '+224' },
        { name: 'Guinea-Bissau', code: '245', flag: '🇬🇼', dialCode: '+245' },
        { name: 'Guyana', code: '592', flag: '🇬🇾', dialCode: '+592' },
        { name: 'Haiti', code: '509', flag: '🇭🇹', dialCode: '+509' },
        {
          name: 'Heard Island and Mcdonald Islands',
          code: '672',
          flag: '🇭🇲',
          dialCode: '+672',
        },
        { name: 'Holy See (Vatican City State)', code: '379', flag: '🇻🇦', dialCode: '+379' },
        { name: 'Honduras', code: '504', flag: '🇭🇳', dialCode: '+504' },
        { name: 'Hong Kong', code: '852', flag: '🇭🇰', dialCode: '+852' },
        { name: 'Hungary', code: '36', flag: '🇭🇺', dialCode: '+36' },
        { name: 'Iceland', code: '354', flag: '🇮🇸', dialCode: '+354' },
        { name: 'India', code: '91', flag: '🇮🇳', dialCode: '+91' },
        { name: 'Indonesia', code: '62', flag: '🇮🇩', dialCode: '+62' },
        { name: 'Iran, Islamic Republic of', code: '98', flag: '🇮🇷', dialCode: '+98' },
        { name: 'Iraq', code: '964', flag: '🇮🇶', dialCode: '+964' },
        { name: 'Ireland', code: '353', flag: '🇮🇪', dialCode: '+353' },
        { name: 'Isle of Man', code: '44', flag: '🇮🇲', dialCode: '+44' },
        { name: 'Israel', code: '972', flag: '🇮🇱', dialCode: '+972' },
        { name: 'Italy', code: '39', flag: '🇮🇹', dialCode: '+39' },
        { name: 'Jamaica', code: '1876', flag: '🇯🇲', dialCode: '+1876' },
        { name: 'Japan', code: '81', flag: '🇯🇵', dialCode: '+81' },
        { name: 'Jersey', code: '44', flag: '🇯🇪', dialCode: '+44' },
        { name: 'Jordan', code: '962', flag: '🇯🇴', dialCode: '+962' },
        { name: 'Kazakhstan', code: '7', flag: '🇰🇿', dialCode: '+7' },
        { name: 'Kenya', code: '254', flag: '🇰🇪', dialCode: '+254' },
        { name: 'Kiribati', code: '686', flag: '🇰🇮', dialCode: '+686' },
        {
          name: "Korea, Democratic People's Republic of",
          code: '850',
          flag: '🇰🇵',
          dialCode: '+850',
        },
        { name: 'Korea, Republic of', code: '82', flag: '🇰🇷', dialCode: '+82' },
        { name: 'Kuwait', code: '965', flag: '🇰🇼', dialCode: '+965' },
        { name: 'Kyrgyzstan', code: '996', flag: '🇰🇬', dialCode: '+996' },
        {
          name: "Lao People's Democratic Republic",
          code: '856',
          flag: '🇱🇦',
          dialCode: '+856',
        },
        { name: 'Latvia', code: '371', flag: '🇱🇻', dialCode: '+371' },
        { name: 'Lebanon', code: '961', flag: '🇱🇧', dialCode: '+961' },
        { name: 'Lesotho', code: '266', flag: '🇱🇸', dialCode: '+266' },
        { name: 'Liberia', code: '231', flag: '🇱🇷', dialCode: '+231' },
        { name: 'Libyan Arab Jamahiriya', code: '218', flag: '🇱🇾', dialCode: '+218' },
        { name: 'Liechtenstein', code: '423', flag: '🇱🇮', dialCode: '+423' },
        { name: 'Lithuania', code: '370', flag: '🇱🇹', dialCode: '+370' },
        { name: 'Luxembourg', code: '352', flag: '🇱🇺', dialCode: '+352' },
        { name: 'Macao', code: '853', flag: '🇲🇴', dialCode: '+853' },
        {
          name: 'Macedonia, the Former Yugoslav Republic of',
          code: '389',
          flag: '🇲🇰',
          dialCode: '+389',
        },
        { name: 'Madagascar', code: '261', flag: '🇲🇬', dialCode: '+261' },
        { name: 'Malawi', code: '265', flag: '🇲🇼', dialCode: '+265' },
        { name: 'Malaysia', code: '60', flag: '🇲🇾', dialCode: '+60' },
        { name: 'Maldives', code: '960', flag: '🇲🇻', dialCode: '+960' },
        { name: 'Mali', code: '223', flag: '🇲🇱', dialCode: '+223' },
        { name: 'Malta', code: '356', flag: '🇲🇹', dialCode: '+356' },
        { name: 'Marshall Islands', code: '692', flag: '🇲🇭', dialCode: '+692' },
        { name: 'Martinique', code: '596', flag: '🇲🇶', dialCode: '+596' },
        { name: 'Mauritania', code: '222', flag: '🇲🇷', dialCode: '+222' },
        { name: 'Mauritius', code: '230', flag: '🇲🇺', dialCode: '+230' },
        { name: 'Mayotte', code: '262', flag: '🇾🇹', dialCode: '+262' },
        { name: 'Mexico', code: '52', flag: '🇲🇽', dialCode: '+52' },
        { name: 'Micronesia, Federated States of', code: '691', flag: '🇫🇲', dialCode: '+691' },
        { name: 'Moldova, Republic of', code: '373', flag: '🇲🇩', dialCode: '+373' },
        { name: 'Monaco', code: '377', flag: '🇲🇨', dialCode: '+377' },
        { name: 'Mongolia', code: '976', flag: '🇲🇳', dialCode: '+976' },
        { name: 'Montenegro', code: '382', flag: '🇲🇪', dialCode: '+382' },
        { name: 'Montserrat', code: '1664', flag: '🇲🇸', dialCode: '+1664' },
        { name: 'Morocco', code: '212', flag: '🇲🇦', dialCode: '+212' },
        { name: 'Mozambique', code: '258', flag: '🇲🇿', dialCode: '+258' },
        { name: 'Myanmar', code: '95', flag: '🇲🇲', dialCode: '+95' },
        { name: 'Namibia', code: '264', flag: '🇳🇦', dialCode: '+264' },
        { name: 'Nauru', code: '674', flag: '🇳🇷', dialCode: '+674' },
        { name: 'Nepal', code: '977', flag: '🇳🇵', dialCode: '+977' },
        { name: 'Netherlands', code: '31', flag: '🇳🇱', dialCode: '+31' },
        { name: 'Netherlands Antilles', code: '599', flag: '🇦🇳', dialCode: '+599' },
        { name: 'New Caledonia', code: '687', flag: '🇳🇨', dialCode: '+687' },
        { name: 'New Zealand', code: '64', flag: '🇳🇿', dialCode: '+64' },
        { name: 'Nicaragua', code: '505', flag: '🇳🇮', dialCode: '+505' },
        { name: 'Niger', code: '227', flag: '🇳🇪', dialCode: '+227' },
        { name: 'Nigeria', code: '234', flag: '🇳🇬', dialCode: '+234' },
        { name: 'Niue', code: '683', flag: '🇳🇺', dialCode: '+683' },
        { name: 'Norfolk Island', code: '672', flag: '🇳🇫', dialCode: '+672' },
        { name: 'Northern Mariana Islands', code: '1670', flag: '🇲🇵', dialCode: '+1670' },
        { name: 'Norway', code: '47', flag: '🇳🇴', dialCode: '+47' },
        { name: 'Oman', code: '968', flag: '🇴🇲', dialCode: '+968' },
        { name: 'Pakistan', code: '92', flag: '🇵🇰', dialCode: '+92' },
        { name: 'Palau', code: '680', flag: '🇵🇼', dialCode: '+680' },
        { name: 'Palestinian Territory, Occupied', code: '970', flag: '🇵🇸', dialCode: '+970' },
        { name: 'Panama', code: '507', flag: '🇵🇦', dialCode: '+507' },
        { name: 'Papua New Guinea', code: '675', flag: '🇵🇬', dialCode: '+675' },
        { name: 'Paraguay', code: '595', flag: '🇵🇾', dialCode: '+595' },
        { name: 'Peru', code: '51', flag: '🇵🇪', dialCode: '+51' },
        { name: 'Philippines', code: '63', flag: '🇵🇭', dialCode: '+63' },
        { name: 'Pitcairn', code: '64', flag: '🇵🇳', dialCode: '+64' },
        { name: 'Poland', code: '48', flag: '🇵🇱', dialCode: '+48' },
        { name: 'Portugal', code: '351', flag: '🇵🇹', dialCode: '+351' },
        { name: 'Puerto Rico', code: '1787', flag: '🇵🇷', dialCode: '+1787' },
        { name: 'Qatar', code: '974', flag: '🇶🇦', dialCode: '+974' },
        { name: 'Reunion', code: '262', flag: '🇷🇪', dialCode: '+262' },
        { name: 'Romania', code: '40', flag: '🇷🇴', dialCode: '+40' },
        { name: 'Russian Federation', code: '7', flag: '🇷🇺', dialCode: '+7' },
        { name: 'Rwanda', code: '250', flag: '🇷🇼', dialCode: '+250' },
        { name: 'Saint Helena', code: '290', flag: '🇸🇭', dialCode: '+290' },
        { name: 'Saint Kitts and Nevis', code: '1869', flag: '🇰🇳', dialCode: '+1869' },
        { name: 'Saint Lucia', code: '1758', flag: '🇱🇨', dialCode: '+1758' },
        { name: 'Saint Pierre and Miquelon', code: '508', flag: '🇵🇲', dialCode: '+508' },
        {
          name: 'Saint Vincent and the Grenadines',
          code: '1784',
          flag: '🇻🇨',
          dialCode: '+1784',
        },
        { name: 'Samoa', code: '685', flag: '🇼🇸', dialCode: '+685' },
        { name: 'San Marino', code: '378', flag: '🇸🇲', dialCode: '+378' },
        { name: 'Sao Tome and Principe', code: '239', flag: '🇸🇹', dialCode: '+239' },
        { name: 'Saudi Arabia', code: '966', flag: '🇸🇦', dialCode: '+966' },
        { name: 'Senegal', code: '221', flag: '🇸🇳', dialCode: '+221' },
        { name: 'Serbia', code: '381', flag: '🇷🇸', dialCode: '+381' },
        { name: 'Seychelles', code: '248', flag: '🇸🇨', dialCode: '+248' },
        { name: 'Sierra Leone', code: '232', flag: '🇸🇱', dialCode: '+232' },
        { name: 'Singapore', code: '65', flag: '🇸🇬', dialCode: '+65' },
        { name: 'Slovakia', code: '421', flag: '🇸🇰', dialCode: '+421' },
        { name: 'Slovenia', code: '386', flag: '🇸🇮', dialCode: '+386' },
        { name: 'Solomon Islands', code: '677', flag: '🇸🇧', dialCode: '+677' },
        { name: 'Somalia', code: '252', flag: '🇸🇴', dialCode: '+252' },
        { name: 'South Africa', code: '27', flag: '🇿🇦', dialCode: '+27' },
        {
          name: 'South Georgia and the South Sandwich Islands',
          code: '500',
          flag: '🇬🇸',
          dialCode: '+500',
        },
        { name: 'Spain', code: '34', flag: '🇪🇸', dialCode: '+34' },
        { name: 'Sri Lanka', code: '94', flag: '🇱🇰', dialCode: '+94' },
        { name: 'Sudan', code: '249', flag: '🇸🇩', dialCode: '+249' },
        { name: 'Suriname', code: '597', flag: '🇸🇷', dialCode: '+597' },
        { name: 'Svalbard and Jan Mayen', code: '47', flag: '🇸🇯', dialCode: '+47' },
        { name: 'Swaziland', code: '268', flag: '🇸🇿', dialCode: '+268' },
        { name: 'Sweden', code: '46', flag: '🇸🇪', dialCode: '+46' },
        { name: 'Switzerland', code: '41', flag: '🇨🇭', dialCode: '+41' },
        { name: 'Syrian Arab Republic', code: '963', flag: '🇸🇾', dialCode: '+963' },
        { name: 'Taiwan, Province of China', code: '886', flag: '🇹🇼', dialCode: '+886' },
        { name: 'Tajikistan', code: '992', flag: '🇹🇯', dialCode: '+992' },
        { name: 'Tanzania, United Republic of', code: '255', flag: '🇹🇿', dialCode: '+255' },
        { name: 'Thailand', code: '66', flag: '🇹🇭', dialCode: '+66' },
        { name: 'Timor-Leste', code: '670', flag: '🇹🇱', dialCode: '+670' },
        { name: 'Togo', code: '228', flag: '🇹🇬', dialCode: '+228' },
        { name: 'Tokelau', code: '690', flag: '🇹🇰', dialCode: '+690' },
        { name: 'Tonga', code: '676', flag: '🇹🇴', dialCode: '+676' },
        { name: 'Trinidad and Tobago', code: '1868', flag: '🇹🇹', dialCode: '+1868' },
        { name: 'Tunisia', code: '216', flag: '🇹🇳', dialCode: '+216' },
        { name: 'Turkey', code: '90', flag: '🇹🇷', dialCode: '+90' },
        { name: 'Turkmenistan', code: '993', flag: '🇹🇲', dialCode: '+993' },
        { name: 'Turks and Caicos Islands', code: '1649', flag: '🇹🇨', dialCode: '+1649' },
        { name: 'Tuvalu', code: '688', flag: '🇹🇻', dialCode: '+688' },
        { name: 'Uganda', code: '256', flag: '🇺🇬', dialCode: '+256' },
        { name: 'Ukraine', code: '380', flag: '🇺🇦', dialCode: '+380' },
        { name: 'United Arab Emirates', code: '971', flag: '🇦🇪', dialCode: '+971' },
        { name: 'United Kingdom', code: '44', flag: '🇬🇧', dialCode: '+44' },
        { name: 'United States', code: '1', flag: '🇺🇸', dialCode: '+1' },
        {
          name: 'United States Minor Outlying Islands',
          code: '1',
          flag: '🇺🇲',
          dialCode: '+1',
        },
        { name: 'Uruguay', code: '598', flag: '🇺🇾', dialCode: '+598' },
        { name: 'Uzbekistan', code: '998', flag: '🇺🇿', dialCode: '+998' },
        { name: 'Vanuatu', code: '678', flag: '🇻🇺', dialCode: '+678' },
        { name: 'Venezuela', code: '58', flag: '🇻🇪', dialCode: '+58' },
        { name: 'Viet Nam', code: '84', flag: '🇻🇳', dialCode: '+84' },
        { name: 'Virgin Islands, British', code: '1284', flag: '🇻🇬', dialCode: '+1284' },
        { name: 'Virgin Islands, U.s.', code: '1340', flag: '🇻🇮', dialCode: '+1340' },
        { name: 'Wallis and Futuna', code: '681', flag: '🇼🇫', dialCode: '+681' },
        { name: 'Western Sahara', code: '212', flag: '🇪🇭', dialCode: '+212' },
        { name: 'Yemen', code: '967', flag: '🇾🇪', dialCode: '+967' },
        { name: 'Zambia', code: '260', flag: '🇿🇲', dialCode: '+260' },
        { name: 'Zimbabwe', code: '263', flag: '🇿🇼', dialCode: '+263' },
      ],
    };
  },
  computed: {
    phoneNumber: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    selectedCountryDialCode() {
      const selectedCountry = this.countries.find(
        (country) => country.code === this.countryCode
      );
      return selectedCountry ? selectedCountry.dialCode : '+234';
    },
    filteredCountries() {
      if (!this.searchQuery) {
        return this.countries;
      }
      const query = this.searchQuery.toLowerCase();
      return this.countries.filter(
        (country) =>
          country.name.toLowerCase().includes(query) ||
          country.dialCode.includes(query) ||
          country.code.includes(query)
      );
    },
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      this.isDropdownOpen = !this.isDropdownOpen;
      if (this.isDropdownOpen) {
        this.$nextTick(() => {
          document.addEventListener('click', this.handleClickOutside);
        });
      }
    },
    handleClickOutside(event) {
      if (
        this.$refs.dropdownContainer &&
        !this.$refs.dropdownContainer.contains(event.target)
      ) {
        this.closeDropdown();
      }
    },
    closeDropdown() {
      this.isDropdownOpen = false;
      this.searchQuery = '';
      document.removeEventListener('click', this.handleClickOutside);
    },
    selectCountry(country) {
      this.$emit('update:countryCode', country.code);
      this.closeDropdown();
    },
    updatePhoneNumber(event) {
      this.$emit('input', event.target.value);
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style scoped>
.phone-input-container {
  display: flex;
  gap: 5px;
  width: 100%;
  position: relative;
}

.country-code-selector {
  flex: 0 0 60px;
  position: relative;
}

.country-code-button {
  width: 100%;
  padding: 8px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: border-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  position: relative;
}

.country-code-button:hover:not(:disabled) {
  border-color: #999;
}

.country-code-button:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.country-code-button.is-open {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.country-code-button.is-invalid {
  border-color: #dc3545;
}

.country-code-button:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.selected-dial-code {
  font-size: 14px;
  color: #333;
  flex: 1;
  text-align: left;
}

.dropdown-arrow {
  color: #666;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.dropdown-arrow.rotate {
  transform: rotate(180deg);
}

.country-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
  background-color: #dc3545;
}

.country-dropdown.is-invalid {
  border-color: #dc3545;
}

.dropdown-search {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
}

.search-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.dropdown-list {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
}

.dropdown-item {
  width: 100%;
  padding: 10px 12px;
  border: none;
  background-color: white;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  transition: background-color 0.15s;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

.dropdown-item.is-selected {
  background-color: #e7f3ff;
  color: #007bff;
}

.country-flag {
  font-size: 18px;
  flex-shrink: 0;
}

.country-name {
  flex: 1;
  text-align: left;
}

.country-dial-code {
  color: #666;
  font-size: 13px;
  flex-shrink: 0;
}

.dropdown-item.is-selected .country-dial-code {
  color: #007bff;
}

.phone-number-input {
  flex: 1;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.form-control:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

/* Scrollbar styling */
.dropdown-list::-webkit-scrollbar {
  width: 8px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.dropdown-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive design */
@media (max-width: 768px) {
  .phone-input-container {
    flex-direction: column;
    gap: 8px;
  }

  .country-code-selector {
    flex: none;
  }
}
</style>
